import { Box } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import {
  css,
  styled as muiStyled,
  styled,
  useTheme,
} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';

import useLeftNavContext from './left-nav-context';
import { NavigationToggleButton } from './navigation-toggle-button';
import Arranger from '../arranger';

import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import { useThemeMode } from '~/client/contexts/theme-context';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';

/**
 * width of navigation menu column when open
 */
const NAV_COLUMN_WIDTH_OPEN = 300;
/**
 * width of navigation menu column when collapsed
 */
const NAV_COLUMN_WIDTH_COLLAPSED = 75;
/**
 * width when completely closed (mobile)
 */
const DRAWER_WIDTH_CLOSE = 0;
/**
 * Drawer breakpoint value - below this, drawer becomes temporary overlay
 */
const DRAWER_BREAKPOINT = 'md';
/**
 * Mobile breakpoint - below this, bottom nav is shown and left nav is hidden
 */
const MOBILE_BREAKPOINT = 'sm';

const PermanentDrawer = muiStyled(MuiDrawer, {
  shouldForwardProp: (prop) =>
    prop !== 'orgCollapsed' && prop !== 'navColumnOpen',
})<{
  open: boolean;
  navColumnOpen: boolean;
}>(({ theme, open, navColumnOpen }) => {
  let width = DRAWER_WIDTH_CLOSE;

  if (open) {
    width = navColumnOpen ? NAV_COLUMN_WIDTH_OPEN : NAV_COLUMN_WIDTH_COLLAPSED;
  }

  return css`
    width: ${width}px;

    .MuiDrawer-paper {
      width: ${width}px;
      transition: ${theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.standard,
      })};
      display: flex;
      flex-direction: row;
      overflow: hidden;
    }
  `;
});

const TemporaryDrawer = muiStyled(MuiDrawer)(({ theme }) => {
  return css`
    .MuiDrawer-paper {
      width: min(${NAV_COLUMN_WIDTH_OPEN}px, 85vw);
      height: 100dvh;
      max-height: 100dvh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  `;
});

const SingleColumnContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  width: '100%',
}));

const NavigationColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tokens' && prop !== 'isDark',
})<{ open: boolean; tokens?: DesignTokenSet; isDark?: boolean }>(({
  theme,
  open,
  tokens,
  isDark,
}) => {
  const t = tokens ?? DesignToken;
  const nav = t.nav;

  return {
    width: open ? NAV_COLUMN_WIDTH_OPEN : NAV_COLUMN_WIDTH_COLLAPSED,
    height: '100%',
    flexShrink: 0,
    // Always use dark sidebar bg so selected/hover overlays stay readable (no white strip)
    backgroundColor: `var(--org-background-color, ${nav.sidebarBg})`,
    backgroundImage: 'none',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    boxShadow: isDark ? nav.sidebarInset : 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '0.5px',
      height: '100%',
      backgroundColor: isDark
        ? 'rgba(255,255,255,0.06)'
        : `var(--org-secondary-color, ${theme.palette.divider})`,
      opacity: isDark ? 1 : 0.2,
    },
  };
});

interface Props extends React.PropsWithChildren {
  className?: string;
  /**
   * Initial route name
   * The name of LeftNavMenu to render initially
   */
  initialRoute: string;
  tokens?: DesignTokenSet;
  /** When set, must match the token set (e.g. effectiveMode === 'dark'). Ensures sidebar background stays in sync with tokens. */
  isDark?: boolean;
}

const LeftNavDrawerImpl: React.FC<Props> = ({
  className,
  initialRoute,
  children,
  tokens,
  isDark: isDarkProp,
}) => {
  const theme = useTheme();
  const isDark = isDarkProp ?? theme.palette.mode === 'dark';
  const isDesktop = useMediaQuery(theme.breakpoints.up(DRAWER_BREAKPOINT));
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLandscape = useMediaQuery('(orientation: landscape)');

  const isTabletLandscape = isTablet && isLandscape;
  const isTabletPortrait = isTablet && !isLandscape;
  const useTemporaryDrawer = isMobile;
  const usePermanentDrawer = !isMobile;

  const { state, jumpTo, setOpen, setNavColumnOpen, setMobileOpen } =
    useLeftNavContext();
  const { open, navColumnOpen, mobileOpen } = state;

  const prevBreakpointRef = React.useRef<{
    isDesktop: boolean;
    isTablet: boolean;
    isMobile: boolean;
  } | null>(null);

  React.useEffect(() => {
    jumpTo(initialRoute);
  }, [initialRoute]);

  React.useEffect(() => {
    const prev = prevBreakpointRef.current;
    if (isMobile) {
      setOpen(false);
    } else if (isTablet) {
      setOpen(true);
      setMobileOpen(false);
      // Only collapse when user resizes from desktop/mobile into tablet. Never collapse on
      // mount/remount or when already in tablet, so expanding then navigating (e.g. Organization
      // submenu) doesn't retract the sidebar.
      const justEnteredTablet = prev !== null && !prev.isTablet;
      if (justEnteredTablet) {
        setNavColumnOpen(false);
      }
    } else if (isDesktop) {
      setOpen(true);
      setMobileOpen(false);
    }
    prevBreakpointRef.current = { isDesktop, isTablet, isMobile };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, isTablet, isMobile]);

  // When temporary drawer is open (tablet/mobile), show expanded content (logo, labels)
  React.useEffect(() => {
    if (useTemporaryDrawer && mobileOpen) {
      setNavColumnOpen(true);
    }
  }, [useTemporaryDrawer, mobileOpen, setNavColumnOpen]);

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  const drawerContent = (
    <SingleColumnContainer
      sx={
        useTemporaryDrawer
          ? { height: '100%', minHeight: 0, flex: '1 1 auto' }
          : undefined
      }
    >
      <NavigationColumn
        open
        id="navigation-column"
        tokens={tokens}
        isDark={isDark}
        sx={
          useTemporaryDrawer
            ? {
                height: '100%',
                minHeight: 0,
                flex: '1 1 auto',
              }
            : undefined
        }
      >
        <Box
          sx={{
            flex: '1 1 0',
            minHeight: 0,
            position: 'relative',
            overflow: 'hidden',
            pt: 1,
          }}
        >
          {children}
        </Box>
      </NavigationColumn>
      {usePermanentDrawer && (
        <NavigationToggleButton
          navColumnOpen={navColumnOpen}
          onToggle={() => setNavColumnOpen(!navColumnOpen)}
        />
      )}
    </SingleColumnContainer>
  );

  if (useTemporaryDrawer) {
    return (
      <TemporaryDrawer
        className={className}
        anchor="left"
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileClose}
      >
        {drawerContent}
      </TemporaryDrawer>
    );
  }

  return (
    <PermanentDrawer
      className={className}
      anchor="left"
      variant="permanent"
      open={open}
      navColumnOpen={navColumnOpen}
    >
      <SingleColumnContainer>
        <NavigationColumn
          open={navColumnOpen}
          id="navigation-column"
          aria-hidden={!navColumnOpen}
          tokens={tokens}
          isDark={isDark}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {children}
          </Box>
        </NavigationColumn>
        <NavigationToggleButton
          navColumnOpen={navColumnOpen}
          onToggle={() => setNavColumnOpen(!navColumnOpen)}
        />
      </SingleColumnContainer>
    </PermanentDrawer>
  );
};

const StyledLeftNavDrawer = muiStyled(LeftNavDrawerImpl)<Props>(({
  theme,
  tokens,
  isDark: isDarkProp,
}) => {
  const t = tokens ?? DesignToken;
  const isDark = isDarkProp ?? theme.palette.mode === 'dark';
  const nav = t.nav;
  const sidebarBg = `var(--org-background-color, ${nav.sidebarBg})`;

  return css`
    .MuiDrawer-paper {
      box-shadow: ${isDark ? 'none' : theme.shadows[1]};
      flex-shrink: 0;
      white-space: pre-wrap;
      overflow-x: hidden;
      background: ${sidebarBg};
      transition: background 0.3s ease;
    }

    /* Keep brand logo at its intended size (matches left-nav-header) */
    .MuiDrawer-paper .brand-button [class*='MuiSvgIcon-root'],
    .MuiDrawer-paper .brand-button .v2-icon {
      font-size: 28px !important;
      width: auto !important;
      height: 40px !important;
    }
  `;
});

/**
 * Left Navigation Drawer
 *
 * A Drawer that is permanently docked to the left side of the screen
 * When width of the screen is above DRAWER_BREAKPOINT, the default
 * state of the drawer is opened
 *
 * The width of the drawer is fixed and can be configured via the constants:
 * - `DRAWER_WIDTH_OPEN`
 * - `DRAWER_WIDTH_CLOSE`
 */
export const LeftNavDrawer: React.FC<Props> = (props) => {
  const { effectiveMode } = useThemeMode();
  const t = useDesignTokens();
  const isDark = effectiveMode === 'dark';
  return <StyledLeftNavDrawer tokens={t} isDark={isDark} {...props} />;
};
